import type { Language, MakePackageId } from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { setLanguage, toLanguage } from "@/i18n";
import { needsWelcome, signsIn, useMe, welcomeUrl } from "./account";
import { type ThemeChoice, useTheme } from "./theme";
import { useTRPC } from "./trpc";

/**
 * Where people sign in, a person's language and theme belong to their account (ADR 0011, 0013):
 * chosen in the welcome steps, changed in the profile or Settings, and applied on every device.
 * Where one person uses the app, the device keeps them and a garage's language decides
 * (architecture.md › Garage settings).
 */

/**
 * Applies the signed-in person's language and theme, and sends someone new from a garage or the
 * admin interface to the welcome steps.
 */
export function AccountPreferences() {
  const { data: me } = useMe();
  const { setTheme } = useTheme();
  const language = me?.language;
  const theme = me?.theme;
  useEffect(() => {
    if (language) setLanguage(language);
  }, [language]);
  useEffect(() => {
    if (theme) setTheme(theme);
  }, [theme, setTheme]);
  // The account pages (sign-in, invitations, the welcome steps themselves) lead on by themselves.
  const onAccountPage = window.location.pathname.startsWith("/account");
  const welcome = Boolean(signsIn() && me && needsWelcome(me) && !onAccountPage);
  useEffect(() => {
    if (welcome) window.location.replace(welcomeUrl());
  }, [welcome]);
  return null;
}

/** Changes the language or theme on this device, and on the account when someone is signed in. */
export function useChangePreferences() {
  const trpc = useTRPC();
  const { data: me } = useMe();
  const { setTheme } = useTheme();
  const save = useMutation({
    ...trpc.account.updatePreferences.mutationOptions(),
    meta: { silent: true },
  });
  const signedIn = signsIn() && me?.status === "active";
  return {
    changeLanguage(language: Language) {
      setLanguage(language);
      if (signedIn) save.mutate({ language });
    },
    changeTheme(theme: ThemeChoice) {
      setTheme(theme);
      if (signedIn) save.mutate({ theme });
    },
  };
}

/**
 * The language of a garage's data (main groups, example data, invitations): its garage
 * setting, else the language on screen. Only inside a garage.
 */
export function useGarageLanguage(): Language {
  const { i18n } = useTranslation();
  const trpc = useTRPC();
  const { data: settings } = useQuery(trpc.settings.get.queryOptions());
  return settings?.language ?? toLanguage(i18n.language);
}

const NO_PACKAGES: MakePackageId[] = [];

/**
 * The make packages a garage loaded, whose main groups and chassis codes are offered
 * (ADR 0014); none until the settings are known. Only inside a garage.
 */
export function useMakePackages(): MakePackageId[] {
  const trpc = useTRPC();
  const { data: settings } = useQuery(trpc.settings.get.queryOptions());
  return settings?.makePackages ?? NO_PACKAGES;
}
